import { CheckableLineItem, LineDamage } from '../../../service/receiving-mobile/log-entry-draft.store';
import {
  isOutstandingLine, isPartsLine, isRefusedOrReturned, dispositionFor, outstandingAmount, expectedQty,
  arrivedQty, netReceivedQty,
} from './missing-page.rules';

/**
 * Compiles the check-off state + manual entry + PO-complete flag into the
 * single "items" string stored on the receiving log row, e.g.:
 *
 *   "LINE ITEMS 1-3, 5, 9 (X2). MISSING LINE ITEMS 4, 9 (X1). PO COMPLETE"
 *
 * The leading list is what ARRIVED in this delivery — complete lines are
 * listed plain (the string itself means "arrived", no COMPLETE suffix needed),
 * partial lines carry what arrived: "(X2)" for a quantity, "(FANS)" for a
 * ragged parts list. The MISSING list is derived (PO vs. logs vs. receipts vs.
 * this check-off, reconciled on the missing page): a wholly-missing line is
 * plain, a partial remainder shows "(X1)" or the user's missing-notes text.
 * Consecutive plain lines collapse into ranges ("1-3"). The whole string is
 * upper-cased. Shared by the Items/Missing steps (preview) and the Review step
 * (submission) so all read the same rule instead of copies drifting apart.
 *
 * When `poComplete` is set there is by definition nothing missing (everything
 * was received, this delivery or earlier), so the MISSING list is omitted —
 * the string never claims "PO COMPLETE" and "MISSING …" at once.
 *
 * Damage is folded INLINE into the two lists rather than a separate clause: a
 * kept-damaged line stays in the arrived list with a "(DAMAGED BUT KEEPING)"
 * qualifier, and a refused/returned line reads in the MISSING list with a
 * "(DAMAGED, REFUSED)" / "(DAMAGED, RETURNED)" qualifier. Plain shortfalls stay
 * unqualified beyond their quantity.
 *
 * @param lineItemRows - PO line items with their in-progress receiving status.
 * @param manualItems - Free-text items entry (adds/corrections), placed after
 *   the line-item lists and before PO COMPLETE.
 * @param poComplete - Whether "This completes the PO" was checked.
 * @param damageByLine - Per-line damage reports, keyed by `lineKey` (optional).
 * @returns The compiled items string.
 */
export function buildItemsString(
  lineItemRows: CheckableLineItem[],
  manualItems: string,
  poComplete: boolean,
  damageByLine: Record<string, LineDamage> = {},
): string {
  const parts: string[] = [];

  const arrived = arrivedEntries(lineItemRows, damageByLine);
  if (arrived.length > 0) {
    parts.push(`${lineItemLabel(arrived.length)} ${renderEntryList(arrived)}`);
  }

  // Additional (off-PO) items collapse into ONE clause, comma-joined, each with
  // its qty as "(x2)" when one was entered — including qty 1 — and its own inline
  // damage suffix. The whole string is upper-cased below, so this reads
  // "ADDITIONAL ITEMS: WIDGET (X1), BRACKET (X4)".
  const offPoRows = lineItemRows.filter((r) => r.isOffPo);
  if (offPoRows.length > 0) {
    const items = offPoRows.map((row) => {
      const qty = String(row.quantity ?? '').trim();
      const qtySuffix = qty ? ` (x${qty})` : '';
      return `${row.description}${qtySuffix}${offPoDamageSuffix(row, damageByLine)}`;
    });
    parts.push(`Additional Items: ${items.join(', ')}`);
  }

  // A complete PO has nothing missing by definition — skip the MISSING list.
  if (!poComplete) {
    const missing = missingClause(lineItemRows, damageByLine);
    if (missing) { parts.push(missing); }
  }

  const manual = manualItems.trim().replace(/"/g, '');
  if (manual) { parts.push(manual); }
  if (poComplete) { parts.push('PO COMPLETE'); }

  return parts.join('. ').toUpperCase();
}

/**
 * The `MISSING LINE ITEM(S) …` clause ALONE, composed from the rows.
 *
 * `buildItemsString` above calls THIS function rather than composing the clause
 * inline, which is the point: a shortfall composed here and one composed anywhere
 * else produce byte-identical text, and cannot drift apart later.
 *
 * It was exported for the log-edit screen, which turned out never to need it: that
 * screen carries the shortfall through as the user's own verbatim text (see
 * `log-edit-items-string.rules.ts` and `missingNoteClause`, which READS a stored
 * clause rather than composing one), and nothing there derives a shortfall from the
 * rows. File-local until something outside actually calls it.
 *
 * NOT upper-cased here — `buildItemsString` upper-cases the whole string at the
 * end, and the edit screen's `recomposeItemsNote` joins this to clauses that are
 * already upper-cased. Callers wanting the stored form should upper-case it, which
 * is what the edit screen does.
 *
 * @returns The clause, or `''` when nothing is outstanding.
 */
function missingClause(
  lineItemRows: CheckableLineItem[],
  damageByLine: Record<string, LineDamage> = {},
): string {
  const missing = missingEntries(lineItemRows, damageByLine);
  if (missing.length === 0) { return ''; }
  return `MISSING ${lineItemLabel(missing.length)} ${renderEntryList(missing)}`;
}

/** Inline damage suffix for an off-PO ADDITIONAL ITEM clause, or '' when undamaged. */
function offPoDamageSuffix(row: CheckableLineItem, damageByLine: Record<string, LineDamage>): string {
  const d = dispositionFor(row, damageByLine);
  if (d === 'Refuse') { return ' (damaged, refused)'; }
  if (d === 'Return') { return ' (damaged, returned)'; }
  if (d === 'Keep') { return ' (damaged but keeping)'; }
  return '';
}

/** A line-number entry in one of the lists, with an optional "(...)" qualifier. */
interface LineEntry {
  line: number;
  /** e.g. "X2" for a quantity, or free text like "FANS"; null = plain number. */
  qualifier: string | null;
}

/**
 * What was KEPT this delivery: the net received amount per line (arrived minus any
 * refused/returned damaged units). A complete line fully received reads plain; a
 * partial (or a complete with some units refused) shows its net "(Xn)". A line
 * whose whole arrival was refused/returned drops off entirely (it reads as a
 * refused/returned clause instead — see `damageClauses`).
 */
function arrivedEntries(rows: CheckableLineItem[], damageByLine: Record<string, LineDamage>): LineEntry[] {
  return rows
    .filter((r) => !r.isOffPo && !r.isCanceled && (r.status === 'complete' || r.status === 'partial'))
    .map((r): LineEntry | null => arrivedEntryFor(r, damageByLine))
    .filter((e): e is LineEntry => e != null)
    .sort((a, b) => a.line - b.line);
}

/**
 * The arrived (kept) entry for one line, or null when nothing was kept. Kept
 * damaged goods (disposition Keep) add a "damaged but keeping" tag alongside the
 * quantity; refused/returned units aren't kept and read in the MISSING list.
 */
function arrivedEntryFor(r: CheckableLineItem, damageByLine: Record<string, LineDamage>): LineEntry | null {
  const refused = isRefusedOrReturned(r, damageByLine);
  const net = netReceivedQty(r, damageByLine); // null = ragged/no count

  // Nothing kept (whole arrival refused/returned) → not in the arrived list.
  if (net != null && net <= 0) { return null; }
  if (net == null && refused) { return null; }

  const kept = dispositionFor(r, damageByLine) === 'Keep';
  let base: string | null;
  if (r.status === 'complete') {
    // Ragged complete (no count) reads plain; countable is plain when fully
    // received, else the kept amount "Xn".
    base = net == null ? null : (net === arrivedQty(r) ? null : `X${net}`);
  } else {
    // Partial — parts list OR a count (mutually exclusive), matching the receipt builder.
    const partsText = (r.partsReceived ?? '').trim();
    base = partsText ? partsText : (net != null ? `X${net}` : null);
  }

  const bits: string[] = [];
  if (base) { bits.push(base); }
  if (kept) { bits.push('damaged but keeping'); }
  return { line: r.line, qualifier: bits.length ? bits.join(', ') : null };
}

/**
 * What's still missing, one entry per outstanding line: the total still owed for
 * the line, with a "damaged, refused" / "damaged, returned" tag when the line was
 * refused/returned. A wholly-missing plain line reads unqualified; a remainder
 * shows its quantity (or, on a ragged parts line, the user's missing-notes text).
 */
function missingEntries(rows: CheckableLineItem[], damageByLine: Record<string, LineDamage>): LineEntry[] {
  const entries: LineEntry[] = [];

  for (const row of rows) {
    if (!isOutstandingLine(row, damageByLine) || row.completesLine) { continue; }

    const disposition = dispositionFor(row, damageByLine);
    const reasonTag = disposition === 'Refuse' ? 'damaged, refused'
      : disposition === 'Return' ? 'damaged, returned'
      : null;

    if (isPartsLine(row)) {
      const notes = (row.missingNotes ?? '').trim();
      const bits = [notes, reasonTag].filter((b): b is string => !!b);
      entries.push({ line: row.line, qualifier: bits.length ? bits.join(', ') : null });
      continue;
    }

    const total = outstandingAmount(row, damageByLine);
    if (total == null || total === 0) { continue; }
    // Show the quantity except for a wholly-missing plain line (nothing arrived,
    // no damage), which reads cleaner as a bare line number.
    const wholeLine = row.status !== 'partial' && !reasonTag && total === expectedQty(row);
    const bits = [wholeLine ? null : `X${total}`, reasonTag].filter((b): b is string => !!b);
    entries.push({ line: row.line, qualifier: bits.length ? bits.join(', ') : null });
  }

  return entries.sort((a, b) => a.line - b.line);
}

function lineItemLabel(count: number): string {
  return count === 1 ? 'LINE ITEM' : 'LINE ITEMS';
}

/**
 * Renders sorted entries as a comma list, collapsing runs of consecutive
 * plain line numbers into ranges: [1,2,3,5,{9,X2}] → "1-3, 5, 9 (X2)".
 * Qualified entries never join a range.
 */
function renderEntryList(entries: LineEntry[]): string {
  const rendered: string[] = [];
  let i = 0;

  while (i < entries.length) {
    const entry = entries[i];
    if (entry.qualifier) {
      rendered.push(`${entry.line} (${entry.qualifier})`);
      i++;
      continue;
    }

    let runEnd = i;
    while (
      runEnd + 1 < entries.length &&
      !entries[runEnd + 1].qualifier &&
      entries[runEnd + 1].line === entries[runEnd].line + 1
    ) {
      runEnd++;
    }

    if (runEnd > i) {
      rendered.push(`${entry.line}-${entries[runEnd].line}`);
    } else {
      rendered.push(`${entry.line}`);
    }
    i = runEnd + 1;
  }

  return rendered.join(', ');
}
